import { useProductEditor } from '../product-editor-context'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { slugify } from '@/lib/utils'

export function PricingSection() {
  const { form, skuUserEdited, setSkuUserEdited } = useProductEditor()

  const originalPrice = form.watch('original_price') || 0
  const featuredDiscount = form.watch('featured_discount_percent') || 0
  const archiveDiscount = form.watch('archive_discount_percent') || 0

  // Calculate preview prices
  const featuredPrice = originalPrice * (1 - featuredDiscount / 100)
  const archivePrice = originalPrice * (1 - archiveDiscount / 100)

  return (
    <Card>
      <CardHeader>
        <CardTitle>Pricing</CardTitle>
        <CardDescription>
          Set the original price and discount percentages
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <FormField
          control={form.control}
          name="original_price"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Original Price ($) *</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="299.99"
                  {...field}
                  onChange={e => field.onChange(parseFloat(e.target.value) || 0)}
                />
              </FormControl>
              <FormDescription>
                The regular retail price before any discounts
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid sm:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="featured_discount_percent"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Featured Discount (%)</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    placeholder="40"
                    {...field}
                    onChange={e => field.onChange(parseInt(e.target.value) || 0)}
                  />
                </FormControl>
                <FormDescription>
                  Discount when featured (This Week's Deal)
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="archive_discount_percent"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Archive Discount (%)</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    placeholder="10"
                    {...field}
                    onChange={e => field.onChange(parseInt(e.target.value) || 0)}
                  />
                </FormControl>
                <FormDescription>
                  Permanent discount in Past Reviews
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="sku"
          render={({ field }) => (
            <FormItem>
              <div className="flex items-center justify-between">
                <FormLabel>SKU</FormLabel>
                {skuUserEdited && (
                  <button
                    type="button"
                    className="text-xs text-red-600 hover:text-red-700"
                    onClick={() => {
                      setSkuUserEdited(false)
                      const title = form.getValues('title')
                      const sku = slugify(title)
                      if (sku) form.setValue('sku', sku)
                    }}
                  >
                    Reset to auto
                  </button>
                )}
              </div>
              <FormControl>
                <Input
                  placeholder="Auto-generated from title"
                  {...field}
                  onChange={e => {
                    field.onChange(e)
                    setSkuUserEdited(true)
                  }}
                />
              </FormControl>
              <FormDescription>
                Auto-generated from title. Edit to customize.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Price Preview */}
        {originalPrice > 0 && (
          <div className="rounded-lg border bg-zinc-50 p-4 space-y-3">
            <h4 className="text-sm font-medium text-zinc-700">Price Preview</h4>

            <div className="grid sm:grid-cols-2 gap-4">
              {/* Featured Price */}
              <div className="space-y-1">
                <div className="text-xs text-zinc-500 uppercase tracking-wide">
                  Featured Price ({featuredDiscount}% off)
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-bold text-red-600">
                    ${featuredPrice.toFixed(2)}
                  </span>
                  {featuredDiscount > 0 && (
                    <span className="text-sm text-zinc-400 line-through">
                      ${originalPrice.toFixed(2)}
                    </span>
                  )}
                </div>
                <div className="text-xs text-green-600">
                  Save ${(originalPrice - featuredPrice).toFixed(2)}
                </div>
              </div>

              {/* Archive Price */}
              <div className="space-y-1">
                <div className="text-xs text-zinc-500 uppercase tracking-wide">
                  Archive Price ({archiveDiscount}% off)
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-bold text-zinc-700">
                    ${archivePrice.toFixed(2)}
                  </span>
                  {archiveDiscount > 0 && (
                    <span className="text-sm text-zinc-400 line-through">
                      ${originalPrice.toFixed(2)}
                    </span>
                  )}
                </div>
                <div className="text-xs text-green-600">
                  Save ${(originalPrice - archivePrice).toFixed(2)}
                </div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
